import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Brain, Sparkles, Globe, ChevronDown } from "lucide-react";

export const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);
  const logoClickTimes = useRef([]);
  const langMenuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Secret triple-click handler for Command Center - Safari compatible
  const handleLogoClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const now = Date.now();
    
    // Add current click time
    logoClickTimes.current.push(now);
    
    // Keep only clicks within last 1000ms (more forgiving for Safari)
    logoClickTimes.current = logoClickTimes.current.filter(time => now - time < 1000);
    
    console.log('Logo clicks:', logoClickTimes.current.length);
    
    // If 3 or more clicks within 1 second
    if (logoClickTimes.current.length >= 3) {
      console.log('Triple click detected! Navigating to Command Center...');
      logoClickTimes.current = []; // Reset
      navigate('/lea-command');
    }
  };

  // Initialize Google Translate Widget
  useEffect(() => {
    // Add Google Translate script
    if (!document.getElementById('google-translate-script')) {
      // Add required CSS to style the widget
      const style = document.createElement('style');
      style.textContent = `
        .goog-te-gadget { font-family: inherit !important; }
        .goog-te-gadget-simple {
          background-color: rgba(255,255,255,0.1) !important;
          border: 1px solid rgba(255,255,255,0.2) !important;
          border-radius: 8px !important;
          padding: 6px 12px !important;
          font-size: 14px !important;
          cursor: pointer !important;
        }
        .goog-te-gadget-simple:hover {
          background-color: rgba(255,255,255,0.2) !important;
        }
        .goog-te-gadget-simple .goog-te-menu-value {
          color: white !important;
        }
        .goog-te-gadget-simple .goog-te-menu-value span {
          color: white !important;
          border: none !important;
        }
        .goog-te-gadget-simple .goog-te-menu-value span:nth-child(3) {
          display: none !important;
        }
        .goog-te-gadget img { display: none !important; }
        .goog-te-banner-frame { display: none !important; }
        body { top: 0 !important; }
        .VIpgJd-ZVi9od-l4eHX-hSRGPd { display: none !important; }
      `;
      document.head.appendChild(style);

      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      document.body.appendChild(script);

      window.googleTranslateElementInit = () => {
        new window.google.translate.TranslateElement({
          pageLanguage: 'fr',
          includedLanguages: 'en,de,es,it,pt',
          layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false
        }, 'google_translate_element');
      };
    }
  }, []);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const links = [
    { href: "/", label: "Accueil" },
    { href: "/formations", label: "Formations" },
    { href: "/formateurs", label: "Nos Formateurs" },
    { href: "/tunnel", label: "Devis" },
    // Blog IA (Robot Speed) caché de la navigation mais route toujours active pour SEO
    { href: "/espace-pedagogique", label: "L'actu de l'IA" },
    { href: "/outils-ia", label: "Outils IA" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      <div className="absolute inset-0 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5" />
      
      <div className="relative max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo - Triple click for secret Command Center */}
          <div 
            onClick={handleLogoClick}
            className="flex items-center gap-3 group cursor-pointer select-none" 
            data-testid="nav-logo"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#007AFF] to-[#007AFF]/50 flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-lg font-bold text-white tracking-tight">Académie</span>
              <span className="text-lg font-bold text-[#007AFF] ml-1">IA</span>
            </div>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {links.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm font-medium ${
                  isActive(link.href) ? "text-white" : ""
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* CTA Button + Language */}
          <div className="hidden md:flex items-center gap-3">
            {/* Google Translate Widget - Visible */}
            <div 
              id="google_translate_element" 
              className="flex items-center"
              data-testid="nav-translate"
            />

            <Link
              to="/tunnel"
              className="btn-primary flex items-center gap-2 text-sm py-3 px-6"
              data-testid="nav-cta"
            >
              <Sparkles className="w-4 h-4" />
              Demander un devis
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 text-white"
            data-testid="nav-mobile-toggle"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden"
            >
              <div className="py-6 space-y-4">
                {links.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={`block text-lg font-medium ${
                      isActive(link.href) ? "text-white" : "text-white/60"
                    }`}
                    data-testid={`nav-mobile-${link.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/tunnel"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary inline-flex items-center gap-2 mt-4"
                  data-testid="nav-mobile-cta"
                >
                  <Sparkles className="w-4 h-4" />
                  Demander un devis
                </Link>
                
                {/* Mobile Language Selector - Using Google Translate */}
                <div className="mt-6 pt-4 border-t border-white/10">
                  <p className="text-white/40 text-sm mb-3">🌐 Traduire / Translate</p>
                  <div id="google_translate_element_mobile" className="mt-2" />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navigation;
